import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fmtErr } from "../../lib/api";
import { Anchor, ArrowRight, MailCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await verifyOtp(email, otp.trim());
      toast.success("Email verified");
      nav("/app", { replace: true });
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!email) return;
    try {
      await resendOtp(email);
      toast.success("New code sent");
      setCooldown(30);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-zinc-50">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-zinc-200 rounded-lg p-8 space-y-6" data-testid="verify-form">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <div>
          <MailCheck className="h-6 w-6 text-ink mb-3" />
          <h1 className="font-display text-2xl font-medium tracking-tight">Verify your email</h1>
          <p className="text-sm text-zinc-600 mt-1">
            We sent a 6-digit code to <span className="font-medium text-ink">{email || "your email"}</span>.
            Enter it below to finish creating your account.
          </p>
        </div>

        {!initialEmail && (
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="verify-email-input" />
          </div>
        )}

        <div>
          <Label htmlFor="otp">Verification code</Label>
          <Input
            id="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            className="font-mono tracking-[0.4em] text-center text-lg"
            required
            data-testid="verify-otp-input"
          />
        </div>

        {err && <div className="text-sm text-red-600" data-testid="verify-error">{err}</div>}

        <Button type="submit" disabled={busy || otp.length !== 6} className="w-full bg-ink hover:bg-black" data-testid="verify-submit">
          {busy ? "Verifying…" : "Verify & continue"} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0}
            className="text-zinc-600 hover:text-ink disabled:text-zinc-400"
            data-testid="resend-otp"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
          <Link to="/login" className="text-zinc-600 hover:text-ink" data-testid="back-to-login">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
